import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import {
  Box,
  IconButton,
  RadioGroup,
} from '@mui/material';
import RateReviewIcon from '@mui/icons-material/RateReview';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import axios from 'src/utils/axios';
import ModalDialogMUI6 from 'src/components/ModalDialogMUI6';
import TooltipMUI6 from 'src/components/TooltipMUI6';
import TextFieldFormik from 'src/components/TextFieldFormik/index';
import { addNotification } from 'src/actions/notificationActions';
import { NOTIFICATION_STATUS } from 'src/constants';

// Session-level cache: the endpoint doubles as the visibility gate (it
// returns enabled=false for everyone except the support account), and the
// options only change via DBeaver edits, so one fetch per session is enough.
let feedbackOptionsPromise = null;

const fetchFeedbackOptions = () => {
  if (!feedbackOptionsPromise) {
    feedbackOptionsPromise = axios
      .get('/api/ai-support-drafts/feedback-options')
      .then((response) => response?.data || { enabled: false, options: [] })
      .catch(() => {
        // Allow a retry on the next mount rather than caching a failure
        feedbackOptionsPromise = null;
        return { enabled: false, options: [] };
      });
  }
  return feedbackOptionsPromise;
};

function AiDraftFeedback({ contactId = null }) {
  const dispatch = useDispatch();
  const [enabled, setEnabled] = useState(false);
  const [options, setOptions] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  // The draft the compose box is showing. Sent with the feedback so the row
  // lands on the draft she actually reviewed; without it the server falls back
  // to whatever is newest at submit time, which on a busy conversation is
  // often a draft written after she started reading.
  const [draftId, setDraftId] = useState(null);
  // What is already filed on this draft, if anything. The control used to look
  // identical before and after submitting, so there was no way to tell a
  // verdict had been recorded - and with editing possible the modal has to
  // open holding it rather than blank.
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    let isMounted = true;
    fetchFeedbackOptions().then((data) => {
      if (!isMounted) return;
      setEnabled(Boolean(data?.enabled));
      setOptions(data?.options || []);
    });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    if (!enabled || !contactId) { setDraftId(null); setExisting(null); return undefined; }
    // One call answers both questions: which draft is current, and what is
    // already on it.
    axios
      .get('/api/ai-support-drafts/feedback', { params: { contactId } })
      .then((response) => {
        if (!isMounted) return;
        setDraftId(response?.data?.aiSupportDraftId || null);
        setExisting(response?.data?.feedback || null);
      })
      .catch(() => {
        // Fall back to the server resolving the draft; feedback still records.
        if (isMounted) { setDraftId(null); setExisting(null); }
      });
    return () => { isMounted = false; };
  }, [enabled, contactId]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      optionCode: existing?.optionCode || '',
      freeText: existing?.freeText || '',
    },
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const { data } = await axios.post('/api/ai-support-drafts/feedback', {
          contactId,
          aiSupportDraftId: draftId,
          optionCode: values.optionCode || null,
          freeText: values.freeText || null,
        });
        setExisting({
          id: data?.id,
          optionCode: values.optionCode || null,
          optionLabel: data?.optionLabel || null,
          freeText: values.freeText || null,
        });
        dispatch(addNotification(
          'ai draft feedback',
          data?.updated ? 'Feedback updated' : 'Feedback saved',
          NOTIFICATION_STATUS.SUCCESS,
        ));
        setIsModalVisible(false);
      } catch (error) {
        const message = error?.response?.data?.message || 'Feedback could not be saved';
        dispatch(addNotification('ai draft feedback', message, NOTIFICATION_STATUS.ERROR));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const {
    values,
    touched,
    errors,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    resetForm,
  } = formik;

  if (!enabled || !contactId) {
    return null;
  }

  const handleOpen = () => setIsModalVisible(true);
  const handleClose = () => {
    // Back to what is filed, not to blank - cancelling an edit should leave
    // the recorded verdict alone.
    resetForm();
    setIsModalVisible(false);
  };

  const isSubmitDisabled = !values.optionCode && !values.freeText.trim();

  return (
    <>
      <TooltipMUI6
        title={existing
          ? `Feedback recorded${existing.optionLabel ? `: ${existing.optionLabel}` : ''} - click to edit`
          : 'AI draft feedback'}
      >
        <IconButton
          aria-label={existing ? 'Edit AI draft feedback' : 'AI draft feedback'}
          size="small"
          color="secondary"
          onClick={handleOpen}
        >
          {/* Filled once something is on file. The outlined and filled icons
              are the same shape, so the control does not move or change size -
              it just stops looking untouched. */}
          {existing
            ? <RateReviewIcon fontSize="small" />
            : <RateReviewOutlinedIcon fontSize="small" />}
        </IconButton>
      </TooltipMUI6>
      <ModalDialogMUI6
        isVisible={isModalVisible}
        title={existing ? 'Edit AI Draft Feedback' : 'AI Draft Feedback'}
        okText={existing ? 'Update' : 'Submit'}
        closeText="Cancel"
        onOk={handleSubmit}
        onClose={handleClose}
        okDisabled={isSubmitDisabled}
        loading={isSubmitting}
        content={(
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              minWidth: '320px',
            }}
          >
            <RadioGroup
              name="optionCode"
              value={values.optionCode}
              onChange={handleChange}
              onBlur={handleBlur}
            >
              {options.map((option) => (
                <TextFieldFormik
                  key={option.code}
                  touchedValue={touched.optionCode}
                  errorValue={errors.optionCode}
                  name="optionCode"
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  value={option.code}
                  formControlLabel={option.label}
                  sizeInput="small"
                  fieldType="radio"
                />
              ))}
            </RadioGroup>
            <TextFieldFormik
              touchedValue={touched.freeText}
              errorValue={errors.freeText}
              name="freeText"
              label="What should it have done differently?"
              handleChange={handleChange}
              handleBlur={handleBlur}
              value={values.freeText}
              sizeInput="small"
              multiline
              minRows={3}
              fullWidth
            />
          </Box>
        )}
      />
    </>
  );
}

AiDraftFeedback.propTypes = {
  contactId: PropTypes.number,
};

export default AiDraftFeedback;
